import React, { useMemo, useState } from 'react';
import { UserPlus, Trash2, LogOut, Crown } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { InviteCoTeacherPanel } from './InviteCoTeacherPanel';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { type Classroom, type TeacherAccount, isTeacherAccount, getClassroomRole, MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
interface ClassroomTeachersTabProps {
  classroom: Classroom;
  activeAccount: TeacherAccount;
}
/**
 * Owner & co-teacher view of the classroom's teachers.
 *
 * - Owner: can invite a co-teacher (via classroom join code), remove a
 *   co-teacher, or transfer ownership.
 * - Co-teacher: read-only list + "Verlaat klas" button.
 *
 * Wave 3.2 — Co-teacher (Gedeelde klassen)
 */
export function ClassroomTeachersTab({
  classroom,
  activeAccount
}: ClassroomTeachersTabProps) {
  const {
    accounts,
    transferClassroomOwnership,
    leaveClassroomAsCoTeacher
  } = useAppStore();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  const [pendingLeave, setPendingLeave] = useState(false);
  const role = getClassroomRole(accounts, classroom.id, activeAccount.id);
  const isOwner = role === 'owner';
  const isCoTeacher = role === 'co-teacher';
  const coTeacherIds = classroom.coTeacherIds ?? [];
  // Build a teacher-id → account lookup so we can render names cleanly.
  const teacherById = useMemo(() => {
    const map: Record<string, TeacherAccount | undefined> = {};
    const ids = new Set<string>([classroom.teacherId, ...coTeacherIds]);
    ids.forEach(id => {
      const acc = accounts.accountsById[id];
      if (acc && isTeacherAccount(acc)) map[id] = acc;
    });
    return map;
  }, [accounts.accountsById, classroom.teacherId, coTeacherIds]);
  const ownerName = teacherById[classroom.teacherId]?.displayName ?? 'Onbekend';
  const handleTransfer = (newOwnerId: string) => {
    transferClassroomOwnership(classroom.id, newOwnerId);
  };
  const handleRemoveCoTeacher = (coTeacherId: string) => {
    // `leaveClassroomAsCoTeacher` simply removes the given teacher from the
    // classroom's coTeacherIds — it works for both self-leave and owner-
    // initiated removal. A dedicated owner-removes-coteacher store action
    // can replace this later without changing the call-site.
    leaveClassroomAsCoTeacher(coTeacherId, classroom.id);
    setPendingRemove(null);
  };
  return <>
      <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-8009">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-8010">
          <div data-id="element-8011">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-8012">
              Docenten
            </h3>
            <p className="text-xs text-slate-400 mt-0.5" data-id="element-8013">
              {classroom.name} · {1 + coTeacherIds.length} docent
              {coTeacherIds.length === 0 ? '' : 'en'}
            </p>
          </div>
          {isOwner && <Button onClick={() => setInviteOpen(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" disabled={coTeacherIds.length >= MAX_CO_TEACHERS_PER_CLASSROOM} title={coTeacherIds.length >= MAX_CO_TEACHERS_PER_CLASSROOM ? `Maximum ${MAX_CO_TEACHERS_PER_CLASSROOM} co-docenten bereikt` : 'Co-docent uitnodigen'} data-id="element-8014">
              <UserPlus className="w-4 h-4" data-id="element-8015" />
              Co-docent uitnodigen
            </Button>}
          {isCoTeacher && <Button onClick={() => setPendingLeave(true)} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 flex items-center gap-2" data-id="element-8016">
              <LogOut className="w-4 h-4" data-id="element-8017" />
              Verlaat klas
            </Button>}
        </div>

        <div className="p-4" data-id="element-8018">
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-8019">
            {/* Owner row */}
            <li className="flex items-center justify-between px-4 py-3" data-id="element-8020">
              <div className="flex items-center gap-3 min-w-0" data-id="element-8021">
                <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-8022">
                  {ownerName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0" data-id="element-8023">
                  <p className="text-sm font-bold text-slate-900 truncate flex items-center gap-2" data-id="element-8024">
                    {ownerName}
                    {classroom.teacherId === activeAccount.id && <span className="text-xs font-medium text-slate-400" data-id="element-8025">
                        (jij)
                      </span>}
                  </p>
                </div>
              </div>
              <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded" data-id="element-8026">
                <Crown className="w-3 h-3" data-id="element-8027" />
                Eigenaar
              </span>
            </li>

            {/* Co-teachers */}
            {coTeacherIds.length === 0 ? <li className="px-4 py-6 text-center bg-slate-50/50" data-id="element-8028">
                <p className="text-sm text-slate-500 font-medium" data-id="element-8029">
                  Nog geen co-docenten
                </p>
                {isOwner && <p className="text-xs text-slate-400 mt-1" data-id="element-8030">
                    Klik op "Co-docent uitnodigen" om een collega toegang te
                    geven.
                  </p>}
              </li> : coTeacherIds.map(coId => {
            const name = teacherById[coId]?.displayName ?? 'Onbekende docent';
            const isSelf = coId === activeAccount.id;
            return <li key={coId} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-8031">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-8032">
                      <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-8033">
                        {name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0" data-id="element-8034">
                        <p className="text-sm font-bold text-slate-900 truncate flex items-center gap-2" data-id="element-8035">
                          {name}
                          {isSelf && <span className="text-xs font-medium text-slate-400" data-id="element-8036">
                              (jij)
                            </span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-8037">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-700 rounded" data-id="element-8038">
                        Co-docent
                      </span>
                      {isOwner && <button onClick={() => setPendingRemove(coId)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Verwijder co-docent" data-id="element-8039">
                          <Trash2 className="w-4 h-4" data-id="element-8040" />
                        </button>}
                    </div>
                  </li>;
          })}
          </ul>
        </div>
      </Card>

      <InviteCoTeacherPanel open={inviteOpen} onClose={() => setInviteOpen(false)} classroom={classroom} activeAccount={activeAccount} teacherById={teacherById} onRequestTransfer={isOwner ? handleTransfer : undefined} data-id="element-8041" />

      {pendingRemove && <ConfirmDialog open={true} title="Co-docent verwijderen?" message={`${teacherById[pendingRemove]?.displayName ?? 'Deze docent'} verliest direct toegang tot ${classroom.name} en de bijbehorende leerlingen.`} confirmLabel="Verwijder" cancelLabel="Annuleren" variant="danger" onConfirm={() => handleRemoveCoTeacher(pendingRemove)} onCancel={() => setPendingRemove(null)} data-id="element-8042" />}

      {pendingLeave && <ConfirmDialog open={true} title="Verlaat deze klas?" message={`Je verliest je toegang tot ${classroom.name}. De eigenaar kan je later opnieuw uitnodigen.`} confirmLabel="Ja, verlaat klas" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      leaveClassroomAsCoTeacher(activeAccount.id, classroom.id);
      setPendingLeave(false);
    }} onCancel={() => setPendingLeave(false)} data-id="element-8043" />}
    </>;
}